import React, { Fragment, useEffect, useRef } from 'react';
import { Script } from 'gatsby';
import PropTypes from 'prop-types';
import classNames from 'classnames';

export const TrustBox = ({ template, height, theme, stars, schemaType, tags, sku, name }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (window.Trustpilot) {
      window.Trustpilot.loadFromElement(ref.current, true);
    }
  }, []);

  return (
    <Fragment>
      <div
        ref={ref}
        className="trustpilot-widget"
        data-locale="en-GB"
        data-template-id={template}
        data-businessunit-id="59397e470000ff0005a42711"
        data-style-height={height}
        data-style-width="100%"
        data-theme={theme ? theme : 'light'}
        data-stars={stars}
        data-schema-type={schemaType}
        data-tags={tags}
        data-sku={sku}
        data-name={name}
      >
        <a href="https://uk.trustpilot.com/review/starlingbank.com" rel="noopener noreferrer" target="_blank">
          Trustpilot reviews
        </a>
      </div>

      <Script src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js" />
    </Fragment>
  );
};

const Carousel = ({ className, hideReadMore, ...props }) => {
  return (
    <section className={className}>
      <TrustBox {...props} />

      {!hideReadMore ? (
        <p className="more">
          <a href="/reviews/">Read more customer reviews...</a>
        </p>
      ) : null}
    </section>
  );
};

const TrustpilotCarousel = ({ type, colourScheme, customPadding }) => {
  const componentClasses = classNames('section-trustpilot section', {
    'section-trustpilot--no-padding-top': customPadding === 'no-top',
    'section-trustpilot--no-padding-bottom': customPadding === 'no-bottom',
    'section-trustpilot--no-padding-top-bottom': customPadding === 'no-padding',
    [`section-trustpilot--${colourScheme}-scheme`]: colourScheme
  });

  switch (type) {
    case 'Mini':
      return <Carousel className={componentClasses} template="53aa8807dec7e10d38f59f32" height="150px" />;
    case 'Slider':
      return <Carousel className={componentClasses} template="54ad5defc6454f065c28af8b" height="240px" stars="4,5" />;
    case 'SliderPCA':
      return (
        <Carousel
          className={componentClasses}
          template="54ad5defc6454f065c28af8b"
          height="240px"
          stars="3,4,5"
          tags="PCA"
        />
      );
    case 'SliderBCA':
      return (
        <Carousel
          className={componentClasses}
          template="54ad5defc6454f065c28af8b"
          height="240px"
          stars="3,4,5"
          tags="BCA"
        />
      );
    case 'SliderSTA':
      return (
        <Carousel
          className={componentClasses}
          template="54ad5defc6454f065c28af8b"
          height="240px"
          sku="STA"
          name="Sole trader current account"
          stars="3,4,5"
        />
      );
    case 'List':
      return <Carousel className={componentClasses} template="539ad60defb9600b94d7df2c" height="500px" stars="4,5" />;
    case 'Micro':
      return (
        <Carousel
          className={componentClasses + ' micro'}
          template="5419b637fa0340045cd0c936"
          height="20px"
          theme="dark"
        />
      );
    case 'MicroDark':
      return <Carousel className={componentClasses + ' micro'} template="5419b637fa0340045cd0c936" height="20px" />;
    case 'GridVerified':
      return (
        <Carousel
          className={componentClasses}
          template="539adbd6dec7e10e686debee"
          height="800px"
          tags="TRUSTPILOT2"
          stars="1,2,3,4,5"
          schemaType="Organization"
          hideReadMore
        />
      );
    case 'PCA':
      return (
        <Carousel
          className={componentClasses}
          template="5717796816f630043868e2e8"
          height="400px"
          sku="PCA"
          name="Personal current account"
        />
      );
    case 'BCA':
      return (
        <Carousel
          className={componentClasses}
          template="5717796816f630043868e2e8"
          height="600px"
          sku="BCA"
          name="Business current account"
        />
      );
    case 'BCAnoSchema':
      return (
        <Carousel
          className={componentClasses}
          template="544a426205dc0a09088833c6"
          height="400px"
          sku="BCA, STA"
          name="Business current account"
        />
      );
    case 'JCA':
      return (
        <Carousel
          className={componentClasses}
          template="5717796816f630043868e2e8"
          height="400px"
          sku="JCA"
          name="Joint current account"
        />
      );
    case 'PCA-EUR':
      return (
        <Carousel
          className={componentClasses}
          template="5717796816f630043868e2e8"
          height="400px"
          sku="PCA-EUR"
          name="Personal euro current account"
        />
      );
    case 'STA':
      return (
        <Carousel
          className={componentClasses}
          template="5717796816f630043868e2e8"
          height="700px"
          sku="STA"
          name="Sole trader current account"
        />
      );
    default:
      return null;
  }
};

TrustpilotCarousel.propTypes = {
  type: PropTypes.oneOf([
    'Mini', //Smallest design with Trustpilot logo, average rating and number of reviews
    'Slider', //Slider design showing verified and unverified reviews (4,5 stars)
    'SliderPCA', //Slider design showing verified PCA reviews (3,4,5 stars)
    'SliderBCA', //Slider design showing verified BCA reviews (3,4,5 stars)
    'SliderSTA', //Verified sole trader account product reviews. No schema. Can be used on landing pages.
    'List', //Vertical list design showing verified and unverified reviews (4,5 stars)
    'Micro', //Small horizontal design showing average rating and review number
    'MicroDark', //Small horizontal design showing average rating and review number
    'GridVerified', //Full page grid layout all verified customer reviews
    'PCA', //Verified personal account product reviews with SEO schema for star ratings. Only use on the main product page. Should only be used once across the site.
    'BCA', //Verified business account product reviews with SEO schema for star ratings. Only use on the main product page. Should only be used once across the site.
    'BCAnoSchema', //Verified business account product reviews
    'JCA', //Verified joint account product reviews with SEO schema for star ratings. Only use on the main product page. Should only be used once across the site.
    'PCA-EUR', //Verified PCA Euro account product reviews with SEO schema for star ratings. Only use on the main product page. Should only be used once across the site.
    'STA' //Verified sole trader account product reviews with SEO schema for star ratings. Only use on the main product page. Should only be used once across the site.
  ]),
  colourScheme: PropTypes.oneOf(['light-grey', 'grey', 'teal', 'white', 'dark-navy', 'sand', 'neutral-navy']),
  customPadding: PropTypes.oneOf(['no-padding', 'no-top', 'no-bottom', 'thin-padding'])
};

export default TrustpilotCarousel;
